import { supabase } from '../api/supabase';

interface VehicleConsistencyReport {
  totalVehicles: number;
  inconsistencies: VehicleInconsistency[];
  summary: {
    hasDriverButAvailable: number;
    noDriverButWorking: number;
    properlyAssigned: number;
  };
}

interface VehicleInconsistency {
  vehicleId: string;
  licensePlate: string;
  makeModel: string;
  currentStatus: string;
  correctStatus: string;
  hasDriver: boolean;
  driverId?: string;
  issueType: 'HAS_DRIVER_BUT_AVAILABLE' | 'NO_DRIVER_BUT_WORKING';
}

/**
 * Comprehensive vehicle-driver assignment consistency checker and fixer
 */
export class VehicleAssignmentAuditor {
  
  /**
   * Scan all vehicles for assignment inconsistencies
   */
  static async auditAllVehicles(): Promise<VehicleConsistencyReport> {
    console.log('🔍 Starting vehicle assignment audit...');
    
    const { data: vehicles, error } = await supabase
      .from('vehicles')
      .select('id, license_plate, make_model, car_status, current_driver_id, fleet_type')
      .order('license_plate');
      
    if (error) {
      throw new Error(`Failed to fetch vehicles: ${error.message}`);
    }
    
    const inconsistencies: VehicleInconsistency[] = [];
    
    for (const vehicle of vehicles) {
      const hasDriver = vehicle.current_driver_id !== null;
      const status = vehicle.car_status;
      
      // Check for inconsistencies (excluding special statuses)
      const isSpecialStatus = ['in_workshop', 'stop', 'cancellation'].includes(status);
      
      if (!isSpecialStatus) {
        if (hasDriver && status === 'available') {
          inconsistencies.push({
            vehicleId: vehicle.id,
            licensePlate: vehicle.license_plate,
            makeModel: vehicle.make_model,
            currentStatus: status,
            correctStatus: 'work',
            hasDriver: true,
            driverId: vehicle.current_driver_id,
            issueType: 'HAS_DRIVER_BUT_AVAILABLE'
          });
        } else if (!hasDriver && status === 'work') {
          inconsistencies.push({
            vehicleId: vehicle.id,
            licensePlate: vehicle.license_plate,
            makeModel: vehicle.make_model,
            currentStatus: status,
            correctStatus: 'available',
            hasDriver: false,
            issueType: 'NO_DRIVER_BUT_WORKING'
          });
        }
      }
    }
    
    const summary = {
      hasDriverButAvailable: inconsistencies.filter(i => i.issueType === 'HAS_DRIVER_BUT_AVAILABLE').length,
      noDriverButWorking: inconsistencies.filter(i => i.issueType === 'NO_DRIVER_BUT_WORKING').length,
      properlyAssigned: vehicles.length - inconsistencies.length
    };
    
    return {
      totalVehicles: vehicles.length,
      inconsistencies,
      summary
    };
  }
  
  /**
   * Fix all inconsistencies found in the audit
   */
  static async fixAllInconsistencies(report: VehicleConsistencyReport): Promise<number> {
    console.log(`🔧 Fixing ${report.inconsistencies.length} inconsistencies...`);
    
    let fixedCount = 0;
    
    for (const inconsistency of report.inconsistencies) {
      try {
        const { error } = await supabase
          .from('vehicles')
          .update({ car_status: inconsistency.correctStatus })
          .eq('id', inconsistency.vehicleId);
          
        if (error) {
          console.error(`❌ Failed to fix ${inconsistency.licensePlate}:`, error.message);
        } else {
          console.log(`✅ Fixed ${inconsistency.licensePlate}: ${inconsistency.currentStatus} → ${inconsistency.correctStatus}`);
          fixedCount++;
        }
      } catch (error) {
        console.error(`❌ Error fixing ${inconsistency.licensePlate}:`, error);
      }
    }
    
    return fixedCount;
  }
  
  /**
   * Run a complete audit and fix cycle
   */
  static async auditAndFix(): Promise<{ report: VehicleConsistencyReport; fixedCount: number }> {
    try {
      // First, run the audit
      const report = await this.auditAllVehicles();
      
      console.log('📊 Audit Results:');
      console.log(`- Total Vehicles: ${report.totalVehicles}`);
      console.log(`- Inconsistencies Found: ${report.inconsistencies.length}`);
      console.log(`- Has Driver but Available: ${report.summary.hasDriverButAvailable}`);
      console.log(`- No Driver but Working: ${report.summary.noDriverButWorking}`);
      console.log(`- Properly Assigned: ${report.summary.properlyAssigned}`);
      
      // If no issues found, return early
      if (report.inconsistencies.length === 0) {
        console.log('🎉 No inconsistencies found - all vehicles are properly assigned!');
        return { report, fixedCount: 0 };
      }
      
      // Fix the issues
      const fixedCount = await this.fixAllInconsistencies(report);
      
      console.log(`\\n🎉 Audit and Fix Complete!`);
      console.log(`- Issues Found: ${report.inconsistencies.length}`);
      console.log(`- Successfully Fixed: ${fixedCount}`);
      console.log(`- Failed to Fix: ${report.inconsistencies.length - fixedCount}`);
      
      return { report, fixedCount };
      
    } catch (error) {
      console.error('❌ Audit and fix failed:', error);
      throw error;
    }
  }
  
  /**
   * Get a summary of current vehicle statuses
   */
  static async getFleetSummary(): Promise<any> {
    const { data: vehicles, error } = await supabase
      .from('vehicles')
      .select('car_status, current_driver_id');
      
    if (error) {
      throw new Error(`Failed to fetch fleet summary: ${error.message}`);
    }
    
    const summary = {
      total: vehicles.length,
      withDrivers: vehicles.filter(v => v.current_driver_id !== null).length,
      withoutDrivers: vehicles.filter(v => v.current_driver_id === null).length,
      statusBreakdown: {} as Record<string, number>,
      driverStatusBreakdown: {
        withDriver: {} as Record<string, number>,
        withoutDriver: {} as Record<string, number>
      }
    };
    
    vehicles.forEach(vehicle => {
      const status = vehicle.car_status || 'unknown';
      const hasDriver = vehicle.current_driver_id !== null;
      
      // Overall status count
      summary.statusBreakdown[status] = (summary.statusBreakdown[status] || 0) + 1;
      
      // Driver-specific status count
      if (hasDriver) {
        summary.driverStatusBreakdown.withDriver[status] = 
          (summary.driverStatusBreakdown.withDriver[status] || 0) + 1;
      } else {
        summary.driverStatusBreakdown.withoutDriver[status] = 
          (summary.driverStatusBreakdown.withoutDriver[status] || 0) + 1;
      }
    });
    
    return summary;
  }
}

/**
 * Quick function to run audit and fix - can be called from anywhere
 */
export const runVehicleAudit = async () => {
  return await VehicleAssignmentAuditor.auditAndFix();
};

/**
 * Quick function to get fleet status
 */
export const getFleetStatus = async () => {
  return await VehicleAssignmentAuditor.getFleetSummary();
};
